// Developer tools, on the board, for a build or an account that has them.
//
// Gated by `useDevTools`: the build-time `DEV_TOOLS` as before, **or** an
// account whose role may `read` `Dev`. That second half is what puts the panel
// on the deployment — before it, the only way to reach these tools was to run
// the repo, so every question that needs them ("what does a tough camp
// do here") could not be asked about the board somebody was actually playing.
//
// It is *also* refused by the server (`grant-card` in `rooms.ts`), because "the
// UI does not offer it" has never been a check: the socket is what a client
// talks to. That refusal is unconditional and is what makes the role gate safe
// to be the only gate — see `useDevTools`.
//
// **The menu and the debug handle no longer agree, on purpose.** They used to
// share one predicate so that neither could appear without the other; the
// split is that this is a *control* and `window.__gambit` is a *console
// convenience* for the smokes, and widening a global handle to every approver
// buys nothing the panel does not already offer.
//
// Fixed to the viewport rather than laid out as a row. Everything in the shell
// that changes size reflows the canvas, and a canvas that resizes throws away
// its drawing buffer — a corner is the one place a control can appear and
// disappear without costing a black frame. **The bottom left**, because the
// left is the corner the ordinary chrome can be pushed off.
//
// **The sections live in `src/client/ai/ui/dev/`**, one file each: this is the shell, the
// gate and the inset registration, and nothing else.
//
// **The panels read `game.board` and send through `game.test`**
// (PLAN-CLIENT-SPLIT day 8). A dev command is the engine's `Command`, which
// the contract does not name — so `TestHandle.dispatch` takes `{type}` and the
// panels build literals against it. `game.test` is `DEV_TOOLS`-gated; on a
// deployment where the role opens this panel it is undefined, and the caller's
// `dispatch` is the fallback until the handle follows the role.

import { useCallback, useEffect, useRef, useState } from "react";

import { PIECE_PACKS, type PiecePack } from "src/client/ai/render/models";
import type { LevelSource } from "src/engine/campaign/levels";
import { ArtifactTokens } from "./dev/ArtifactTokens";
import { BeatArmy } from "./dev/BeatArmy";
import { GrantCards } from "./dev/GrantCards";
import { GrowBoard } from "./dev/GrowBoard";
import { LoadLevel } from "./dev/LoadLevel";
import { Caption, Fold, PanelProvider, Row } from "./dev/parts";
import { ResetOnboarding } from "./dev/ResetOnboarding";
import { StockPiece } from "./dev/StockPiece";
import { Btn, Divider, HoverCard, Panel, Segmented, styled, useChromeRect } from "./kit";
import { useDevTools } from "./useDevTools";
import { useGame } from "./useGame";
import { useSessionView } from "./useSessionView";

/**
 * What a panel sends: the engine's command, as the contract carries it — a
 * `type` and whatever the reducer's case reads. The panels build literals; the
 * reducer refuses what it does not know.
 */
export type DevDispatch = (command: { type: string; [field: string]: unknown }) => void;

/**
 * How wide the dev panel is.
 *
 * Nobody else reads it: the panel registers the rect it covers with the inset
 * registry (`panelRef` below), so the camera's inset follows the width rather
 * than being told it. Widened from 268 once the cards became a grid: at that
 * width a tile grid is one column, which is a list with extra steps.
 */
const PANEL_WIDTH = 360;

const Corner = styled("div")(({ theme }) => ({
  position: "fixed",
  left: theme.kit.space.lg,
  bottom: theme.kit.space.lg,
  zIndex: theme.kit.z.dev,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.kit.space.sm
}));

/**
 * The sheet.
 *
 * Bounded by the window rather than by its own content: it grows a row every
 * time the dev tools do, and unreachable rather than ugly was the old failure —
 * Playwright reported the grow controls as "outside of the viewport", which is
 * exactly what a person clicking at them would have found.
 */
const Sheet = styled(Panel)(({ theme }) => ({
  width: PANEL_WIDTH,
  boxSizing: "border-box",
  padding: theme.kit.space.md,
  maxHeight: "calc(100vh - 96px)",
  overflowY: "auto",
  boxShadow: `0 12px 32px color-mix(in srgb, ${theme.kit.color.shadow} 70%, transparent)`
}));

/** The handle. A square, because it is an icon and not a word. */
const Handle = styled(Btn)({ width: 44, height: 44, padding: 0, fontSize: 20 });

const PACKS = PIECE_PACKS.map((id) => ({
  value: id,
  label: id,
  testId: `dev-pack-${id}`
}));

const CELEBRATIONS = [
  { kind: "stage" as const, label: "Front cleared" }
];

export function DevMenu({
  dispatch: fallback,
  onCelebrate,
  onLoadLevel,
  planning = false,
  canPlan = false,
  onPlanning,
  pack = "base",
  onPack
}: {
  /** Ignored: the board is `game.board`'s now. Kept so the caller compiles. */
  state?: unknown;
  /**
   * Where a command goes when `game.test` is not there — a deployed build
   * whose role opened the panel. Optional, and the handle wins where it exists.
   * Typed as taking `never` so the caller's `Command`-typed door still fits:
   * the panels' literals are checked against `DevDispatch`, not against this.
   */
  dispatch?: (command: never) => unknown;
  /** Ignored: the selection is `SessionView.selected`. Kept so the caller compiles. */
  selected?: number;
  /** Whether the planning drawer is showing. */
  planning?: boolean;
  /**
   * Is there a machine whose plan there is to show?
   *
   * Hot-seat has no opponent to spy on, so the control cannot mean anything —
   * and a button that flips its own label and does nothing is the worst
   * version of that. Disabled, and it says why.
   */
  canPlan?: boolean;
  /**
   * Show what the opponent is thinking, while it is thinking.
   *
   * The planning layer is the only part of this game with no visible output —
   * a belt shows its cargo, a foundry shows its recipe, a march draws itself,
   * and the brief that decides every turn lives for one turn inside a worker
   * and is seen by nobody. Three of this project's longer debugging sessions
   * were numbers nobody was looking at.
   */
  onPlanning?(on: boolean): void;
  /** Which piece set is drawn. See `PIECE_PACKS`. */
  pack?: PiecePack;
  /**
   * Swap the piece models.
   *
   * Developer-only, because a pack is art in progress: heights and footprints
   * are cleaned to the base set's, but a generated set still has ranks that
   * read short and a queen that arrived a day after the rest. A player picking
   * one would be choosing between a finished set and an unfinished one.
   */
  onPack?(pack: PiecePack): void;
  /**
   * Show a celebration without earning one.
   *
   * Not a command, because a celebration is not game state — it is derived
   * from what a turn reported and from the log, and inventing a fake one
   * through the reducer would put a lie in the save format. This hands the
   * queue a preview directly.
   */
  onCelebrate?(kind: "stage"): void;
  /**
   * Open a level in place of the board.
   *
   * Not a command either: opening a level is the `new-game` the run's own
   * loader sends, so this hands that loader a source (`App.tsx`). Absent
   * outside a run, and the section says so.
   */
  onLoadLevel?(source: LevelSource): void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  // What the panel covers, said by the panel. A callback ref, because the thing
  // being measured is rendered conditionally — an effect over a `RefObject`
  // never sees an element that mounts after it (see `useChromeRect`).
  const panelRef = useChromeRect("dev-panel", "left");
  // The sheet itself, so every hover card inside it can open *beside the
  // panel* rather than onto the row next to its trigger (see `dev/parts.tsx`).
  // State and not a ref: Popper needs the element on the render that opens.
  const [sheet, setSheet] = useState<HTMLElement | null>(null);
  const holdSheet = useCallback(
    (node: HTMLDivElement | null) => {
      setSheet(node);
      panelRef(node);
    },
    [panelRef]
  );
  // Above every early return, since the answer arrives a tick after mount on a
  // deployed build and a hook cannot be called conditionally.
  const devTools = useDevTools();
  const game = useGame();
  const { selected } = useSessionView();
  const dispatch: DevDispatch = (command) => {
    if (game.dev) game.dev.dispatch(command);
    else if (fallback) fallback(command as never);
    else console.warn("dev: no door for a command on this build", command.type);
  };

  // Click away to close, like any menu. On pointerdown so it resolves before
  // the board's own click handler runs — the same reason the piece badges
  // dismiss that way.
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  if (!devTools) return null;

  const run = game.board.facts().run;

  return (
    <Corner ref={root}>
      {open && (
        // Every hover card inside the sheet opens beside the sheet, never
        // beside its own trigger — `dev/parts.tsx` says why.
        <PanelProvider value={sheet}>
        <Sheet data-testid="dev-panel" ref={holdSheet}>
          <Caption>Developer tools</Caption>
          <Divider />

          {/* The two things that are hard to reach on purpose: a prize takes a
              whole haul to earn and a stage takes a whole war. */}
          <Fold id="celebrate" title="Celebrate">
            <Row>
              {CELEBRATIONS.map((entry) => (
                <Btn
                  key={entry.kind}
                  small
                  data-testid={`dev-celebrate-${entry.kind}`}
                  onClick={() => onCelebrate?.(entry.kind)}
                >
                  {entry.label}
                </Btn>
              ))}
            </Row>
          </Fold>

          <Fold id="pack" title="Piece set">
            <Segmented small value={pack} options={PACKS} onChange={(id) => onPack?.(id)} />
          </Fold>

          {/* What the opponent is thinking, while it is thinking. A toggle
              rather than a button: it is a view you leave on while you watch. */}
          <Fold id="planning" title="Planning">
            <HoverCard
              placement="right"
              beside={sheet}
              title="The opponent's plan"
              card={
                canPlan
                  ? "The brief behind every turn, while the worker is still holding it."
                  : "Nobody here is played by a machine."
              }
            >
              <Btn
                small
                variant={planning ? "primary" : "default"}
                data-testid="dev-planning"
                aria-pressed={planning}
                disabled={!canPlan}
                onClick={() => onPlanning?.(!planning)}
              >
                {!canPlan
                  ? "No machine to watch"
                  : planning
                    ? "Hide the opponent's plan"
                    : "Show the opponent's plan"}
              </Btn>
            </HoverCard>
          </Fold>

          <LoadLevel inRun={Boolean(run)} onLoad={onLoadLevel} />
          <ResetOnboarding />
          <GrowBoard dispatch={dispatch} />
          <BeatArmy dispatch={dispatch} />
          <StockPiece dispatch={dispatch} selected={selected} />
          {run && <ArtifactTokens dispatch={dispatch} side={run.side} />}
          <GrantCards dispatch={dispatch} inRun={Boolean(run)} />
        </Sheet>
        </PanelProvider>
      )}

      <Handle
        data-testid="dev-menu"
        aria-label="Developer tools"
        aria-expanded={open}
        variant={open ? "primary" : "default"}
        onClick={() => setOpen((was) => !was)}
      >
        ☰
      </Handle>
    </Corner>
  );
}
